import type { AiCreditsIncentiveId } from '../models/ai-credits-incentive.model';

export function isValidHttpUrl(value: string): boolean {
	try {
		const url = new URL(value.trim());
		return url.protocol === 'https:' || url.protocol === 'http:';
	} catch {
		return false;
	}
}

/**
 * Reduces an account domain to a bare hostname for comparison.
 *
 * The account domain is user entered, so it may arrive as a full URL
 * (`https://www.acme.com/about`), a bare host (`acme.com`) or with a leading
 * `www.`. Returns null when the value cannot be read as a real domain, which
 * callers treat as "no domain on file" rather than as a failed check.
 */
export function normaliseDomain(value: string | null | undefined): string | null {
	const trimmed = value?.trim().toLowerCase();
	if (!trimmed) {
		return null;
	}

	let hostname: string;
	try {
		const withProtocol = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
		hostname = new URL(withProtocol).hostname;
	} catch {
		return null;
	}

	const bare = hostname.replace(/^www\./, '');

	// A domain without a dot (`localhost`, or a half-entered value) is not
	// something we can meaningfully match a submitted page against.
	return bare.includes('.') ? bare : null;
}

/**
 * True when `hostname` is the account domain itself or a subdomain of it, so a
 * link from `blog.acme.com` still counts for an account on `acme.com`.
 */
export function isSameOrSubdomain(hostname: string, accountDomain: string): boolean {
	const host = hostname.toLowerCase().replace(/^www\./, '');
	return host === accountDomain || host.endsWith(`.${accountDomain}`);
}

/**
 * Builds the full proof URL from the account domain and the path typed after
 * the fixed prefix.
 *
 * People paste whole URLs into path fields out of habit, so a pasted absolute
 * URL is reduced to its path rather than rejected or concatenated into
 * nonsense. Returns null when there is no usable account domain.
 */
export function buildAccountPathUrl(
	accountDomain: string | null | undefined,
	path: string
): string | null {
	const domain = normaliseDomain(accountDomain);
	if (!domain) {
		return null;
	}

	let suffix = path.trim();

	// A pasted absolute URL contributes only its path, query and hash.
	if (/^[a-z][a-z0-9+.-]*:\/\//i.test(suffix)) {
		try {
			const pasted = new URL(suffix);
			suffix = `${pasted.pathname}${pasted.search}${pasted.hash}`;
		} catch {
			return null;
		}
	}

	suffix = suffix.replace(/^\/+/, '');

	return `https://${domain}/${suffix}`;
}

export function validateIncentiveProofUrl(
	incentiveId: AiCreditsIncentiveId,
	value: string,
	accountDomain?: string | null
): string | null {
	const trimmed = value.trim();
	if (!trimmed) {
		return 'URL is required';
	}
	if (!isValidHttpUrl(trimmed)) {
		return 'Enter a valid URL starting with https://';
	}

	const hostname = new URL(trimmed).hostname.toLowerCase();

	if (incentiveId === 'g2-review' && !hostname.endsWith('g2.com')) {
		return 'Enter a published G2 review URL';
	}

	if (incentiveId === 'link-back') {
		// Only enforce when we have a usable domain on file. Accounts without
		// one fall back to open submission rather than being locked out.
		const expectedDomain = normaliseDomain(accountDomain);
		if (expectedDomain && !isSameOrSubdomain(hostname, expectedDomain)) {
			return `This needs to be a page on ${expectedDomain}`;
		}
	}

	return null;
}
